<script setup lang="ts">
import { computed } from "vue";

import type { ButtonAltProps } from "@/dsComponents/buttons/types";
import type { TabProps } from "@/dsComponents/Tab/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Tab from "@/dsComponents/Tab/Tab.vue";

export interface ProfileSectionTitleProps {
    label: string;
    icon?: string;
}

export interface ProfileSectionActionProps extends ButtonAltProps {
    onClick?: () => void;
}

export interface ProfileSectionTabProps extends TabProps {
    onSelectTab?: (tab: string | number) => void;
}

export interface ProfileSectionProps {
    title: ProfileSectionTitleProps;
    subtitle?: ProfileSectionTitleProps;
    actions?: ProfileSectionActionProps[];
    tabs?: ProfileSectionTabProps;
    sectionGap?: string;
    noContentBorder?: boolean;
}

const props = defineProps<ProfileSectionProps>();

const rootStyle = computed(() => (props.sectionGap ? { gap: props.sectionGap } : undefined));
const getActionButtonProps = (action: ProfileSectionActionProps) => {
    const { onClick: _onClick, ...buttonProps } = action;
    return buttonProps;
};
</script>

<template>
    <div class="profile-section" :style="rootStyle">
        <div class="profile-section__top">
            <div class="profile-section__header">
                <div class="profile-section__title">
                    <div class="profile-section__title__main-title">
                        <i v-if="title.icon" :class="`ti ti-${title.icon} profile-section__title__main-title__icon`" />
                        <span class="profile-section__title__main-title__label typo-md-medium">
                            {{ title.label }}
                        </span>
                    </div>
                    <div v-if="subtitle" class="profile-section__title__sub-title">
                        <i v-if="subtitle.icon" :class="`ti ti-${subtitle.icon} profile-section__title__sub-title__icon`" />
                        <span class="profile-section__title__sub-title__label typo-xs-regular">
                            {{ subtitle.label }}
                        </span>
                    </div>
                </div>

                <div v-if="actions?.length || $slots.actions" class="profile-section__actions">
                    <slot name="actions">
                        <ButtonAlt
                            v-for="action in actions"
                            :key="action.label"
                            v-bind="getActionButtonProps(action)"
                            size="sm"
                            type="neutral"
                            variant="filled"
                            @click="action.onClick?.()"
                        />
                    </slot>
                </div>
            </div>

            <slot name="top">
                <Tab
                    v-if="tabs"
                    :tab-groups="tabs.tabGroups"
                    :border-bottom="tabs.borderBottom"
                    :no-padding="tabs.noPadding"
                    @select:tab="tabs.onSelectTab"
                />
            </slot>
        </div>

        <div v-if="$slots.content" :class="['profile-section__wrapper', { 'profile-section__wrapper--no-border': noContentBorder }]">
            <slot name="content" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.profile-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md, 8px);

    &__top {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: var(--spacing-xs, 4px);
        min-height: 32px;
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-height: 32px;
    }

    &__title {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: var(--spacing-xxs, 2px);
        flex: 1 0 0;

        &__main-title,
        &__sub-title {
            display: flex;
            align-items: center;
            gap: var(--spacing-md, 8px);

            &__icon {
                font-size: 14px;
                color: var(--icon-sub-500, #636778);
            }

            &__label {
                color: var(--text-sub-600, #494e61);
            }
        }

        &__main-title {
            min-height: 28px;
        }
    }

    &__actions {
        display: flex;
        align-items: flex-start;
        justify-content: flex-end;
        gap: var(--spacing-md, 8px);
        flex-shrink: 0;
    }

    &__wrapper {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs, 4px);
        padding: var(--spacing-md, 8px);
        border-radius: var(--radius-xl, 12px);
        border: 1px solid var(--stroke-soft-200, #dbdfe6);
        background: var(--bg-white-0, #fff);
        box-shadow: var(--regular-shadow-xs, 0px 1px 2px 0px #dbdfe640);

        &--no-border {
            padding: 0;
            border: none;
            background: transparent;
            box-shadow: none;
        }
    }
}
</style>
